import { PrinterProfile } from '../../types/printerProfile';
import { getRoutingConfig } from '../../services/printerRoutingService';
import { KioskTicketInput, toPortedTicketData } from './adapter';
import { setRasterRoutingSnapshot, clearRasterRoutingSnapshot } from './escposRaster';
import { buildUnifiedTicketFromProfile } from './ticketFormatterUnified';
import { buildEpsonTicket, buildStarTicket } from './ticketFormatterBrand';
import { buildNoAnchorTicket } from './ticketFormatterNoAnchor';
import { buildAntiPatternTicket } from './ticketFormatterAntiPattern';
import { buildRasterTicket } from './ticketFormatterRaster';
import { buildRasterHybridTicket } from './ticketFormatterRasterHybrid';

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * AIGUILLEUR DES MODULES PORTÉS — point d'entrée UNIQUE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Sept modules d'impression développés côté console, transposés ici. Ils sont
 * STRICTEMENT ADDITIFS : tant qu'aucun profil de ce type n'est activé, la borne
 * imprime exactement comme avant (legacy / modern / custom / minimal / défaut).
 *
 * L'exploitant choisit le module dans « Périphériques » → « Profils d'impression »,
 * en activant le profil correspondant. Rien n'est exposé au client final.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * CE QUE CHAQUE MODULE RÉSOUT (contrat du pont Android, source v5.8)
 * ───────────────────────────────────────────────────────────────────────────────
 * `unified`      Un seul rendu régulier sur les QUATRE pilotes natifs (ESC/POS,
 *                Star USB, Star Bluetooth, Sunmi) : tout le remplissage est calculé
 *                en JS, jamais délégué aux colonnes natives. Le plus sûr en production.
 * `epson`/`star` Rendu dédié par marque : colonnes NATIVES `[L]…[R]` (le pont aligne
 *                à SA largeur), octets de taille pour Epson, zéro octet pour Star.
 * `noanchor`     Colonnes natives + interdiction absolue du mot `ARTICLES` (le verrou
 *                `MainActivity:4826` qui détruit la colonne des prix reste inerte).
 * `antipattern`  Contre-test : ancre armée, mais quantité écrite `2× Nom` — la regex
 *                Java `.*x\d+.*` ne matche pas le `×` (U+00D7).
 * `raster`       Ticket ENTIER en image `GS v 0` : aucune règle de réécriture ne peut
 *                atteindre ce qui n'est pas du texte. Fidélité absolue, volume élevé.
 * `raster-hybrid` Seul le bloc articles passe en image (~10 % du volume).
 *
 * ⚠️ `raster` et `raster-hybrid` REFUSENT l'image sur Star / Sunmi (l'hexadécimal y
 * sortirait en clair) et se rabattent alors sur `noanchor`. Ce refus s'appuie sur un
 * instantané du routage pris par `preparePortedTicket()` — d'où l'obligation de
 * l'appeler AVANT `buildPortedTicket()`.
 */

export const PORTED_PROFILE_TYPES = [
  'unified',
  'epson',
  'star',
  'noanchor',
  'antipattern',
  'raster',
  'raster-hybrid',
] as const;

export type PortedProfileType = (typeof PORTED_PROFILE_TYPES)[number];

export const isPortedProfileType = (type: unknown): type is PortedProfileType =>
  typeof type === 'string' && (PORTED_PROFILE_TYPES as readonly string[]).includes(type);

/** Libellés d'écran des modules portés (français ; passer par `t()` à l'affichage). */
export const PORTED_TYPE_LABELS: Record<PortedProfileType, string> = {
  unified: 'Unifié (tous pilotes)',
  epson: 'Epson (ESC/POS)',
  star: 'Star (TSP)',
  noanchor: 'Colonnes natives',
  antipattern: 'Anti-motif',
  raster: 'Image complète',
  'raster-hybrid': 'Image hybride',
};

/**
 * Prend l'instantané des cibles d'impression AVANT de composer un ticket.
 *
 * ⚠️ À `await` systématiquement : sans lui, les modules image se replient sur le texte
 * (voir `escposRaster.checkRasterTargets`). C'est un repli SÛR, pas une panne, mais on
 * perd le bénéfice de l'image.
 */
export const preparePortedTicket = async (): Promise<void> => {
  try {
    const config = await getRoutingConfig();
    setRasterRoutingSnapshot([
      config?.mainPrinterId,
      ...(Array.isArray(config?.categoryMappings)
        ? config.categoryMappings.map((mapping) => mapping?.printerId)
        : []),
    ]);
  } catch (error) {
    // Routage illisible : on REFUSE l'image plutôt que de parier sur le type d'imprimante.
    console.warn('[ticketModules] Routage illisible, rendu image désactivé:', error);
    clearRasterRoutingSnapshot();
  }
};

/**
 * Construit le ticket d'un module porté.
 * `data` est l'objet ticket de la BORNE : la conversion est faite par `adapter.ts`.
 */
export const buildPortedTicket = (
  type: PortedProfileType,
  data: KioskTicketInput,
  profile?: PrinterProfile
): string => {
  const ported = toPortedTicketData(data);

  switch (type) {
    case 'unified':
      return buildUnifiedTicketFromProfile(ported, profile);
    case 'epson':
      return buildEpsonTicket(ported, profile);
    case 'star':
      return buildStarTicket(ported, profile);
    case 'noanchor':
      return buildNoAnchorTicket(ported, profile);
    case 'antipattern':
      return buildAntiPatternTicket(ported, profile);
    case 'raster':
      return buildRasterTicket(ported, profile);
    case 'raster-hybrid':
      return buildRasterHybridTicket(ported, profile);
    default:
      return buildUnifiedTicketFromProfile(ported, profile);
  }
};

export type { KioskTicketInput } from './adapter';
